import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Palette } from "lucide-react";
import { IconBadge } from "./IconBadge";
import { ThemePicker } from "./ThemePicker";
import { FeedbackButton } from "./FeedbackButton";
import { navRoutes } from "@/lib/nav-routes";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { isAdmin } from "@/lib/admin";
import { usePlausible } from "@/hooks/usePlausible";

function getInitials(username: string | null | undefined, email: string | null | undefined): string {
  if (username) return username[0].toUpperCase();
  if (email) return email[0].toUpperCase();
  return "?";
}

export function Sidebar({ activeNav }: { activeNav: string }) {
  const [, navigate] = useLocation();
  const { currentUser, signOut } = useAuth();
  const { profile } = useUserProfile();
  const [showThemePicker, setShowThemePicker] = useState(false);
  const { trackEvent } = usePlausible();

  const visibleRoutes = navRoutes.filter(
    ({ label }) => label !== "Harvest" || isAdmin(currentUser?.uid),
  );

  const avatarSrc = profile?.avatarUrl ? `/api/storage${profile.avatarUrl}` : null;
  const displayName = profile?.username ?? currentUser?.email ?? "Signed in";
  const initials = getInitials(profile?.username, currentUser?.email);

  return (
    <aside
      className="sticky top-5 hidden h-screen max-h-[calc(100vh-2.5rem)] w-72 shrink-0 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm lg:flex lg:flex-col"
      data-testid="sidebar"
    >
      <div className="mb-8 flex items-center gap-3">
        <IconBadge dark>↗</IconBadge>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold tracking-tight text-stone-900">QuotedsArrow</h1>
          <p className="text-xs text-stone-500">Words with Pointed Purpose</p>
        </div>
        <button
          onClick={() => setShowThemePicker((v) => !v)}
          aria-label="Change mood theme"
          aria-pressed={showThemePicker}
          title="Change mood theme"
          className={
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 " +
            (showThemePicker
              ? "bg-stone-900 text-white"
              : "text-stone-400 hover:bg-stone-100 hover:text-stone-700")
          }
        >
          <Palette className="h-4 w-4" />
        </button>
      </div>

      {showThemePicker && (
        <div className="mb-5 rounded-2xl border border-stone-100 bg-stone-50 p-3">
          <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400">
            Mood
          </p>
          <ThemePicker compact />
        </div>
      )}

      <nav className="flex-1 space-y-2">
        {visibleRoutes.map(({ label, path }) => {
          const isActive = activeNav === label;
          return (
            <button
              key={label}
              onClick={() => { if (path === "/discover") trackEvent("Discovery Used"); navigate(path); }}
              className={
                "flex w-full items-center justify-between rounded-2xl px-4 py-3 text-sm font-medium transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 " +
                (isActive
                  ? "bg-stone-900 text-white shadow-sm active:bg-stone-800"
                  : "text-stone-600 hover:bg-stone-100 hover:text-stone-900 active:bg-stone-200")
              }
              data-testid={`nav-${label}`}
            >
              <span>{label}</span>
              <span className="text-xs opacity-60">→</span>
            </button>
          );
        })}
      </nav>

      <div className="mt-4 border-t border-stone-100 pt-4">
        <div className="mb-3 flex items-center gap-2.5 px-1">
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={displayName}
              className="h-7 w-7 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-200 text-xs font-semibold text-stone-600">
              {initials}
            </div>
          )}
          <p className="truncate text-xs text-stone-600 font-medium">{displayName}</p>
        </div>
        <FeedbackButton variant="sidebar" />
        <button
          onClick={signOut}
          className="flex w-full items-center justify-between rounded-2xl px-4 py-2.5 text-sm font-medium text-stone-500 transition hover:bg-stone-100 hover:text-stone-900 active:scale-[0.98] active:bg-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
          data-testid="btn-sign-out"
        >
          <span>Sign out</span>
          <span className="text-xs opacity-60">↩</span>
        </button>
        <div className="mt-3 flex items-center gap-3 px-1">
          <Link href="/privacy" className="text-[11px] text-stone-400 hover:text-stone-600 transition-colors">Privacy</Link>
          <span className="text-stone-300 text-[11px]">·</span>
          <Link href="/terms" className="text-[11px] text-stone-400 hover:text-stone-600 transition-colors">Terms</Link>
        </div>
      </div>
    </aside>
  );
}
